<template>
  <div :class="[{'p-5': subscriptionWidgetClass}]">
    <template v-if="subscriptionProgressData === null">
      <slot name="loading">
        <NewsletterFormInputsSkeleton />
      </slot>
    </template>
    <template v-if="showSubscriptionForm">
      <slot name="header" />
      <form
        action=""
        :class="formClasses"
        @submit.stop.prevent="onFormSubmit"
      >
        <label
          :for="inputId"
          class="sr-only text-EXPONDO-white"
        >
          {{ $t("Email for newsletter") }}
        </label>
        <div
          class="relative"
          :class="[{'flex flex-row gap-2.5': isSubscriptionWidget}]"
        >
          <PrimaryInput
            :id="inputId"
            v-model="email"
            name="newsletter-email"
            :placeholder="$t('Newsletter Placeholder').toString()"
            required
            class="newsletter__input box-border w-full"
            :class="{'lg:min-w-[370px]': isSubscriptionWidget}"
            @update:model-value="onEmailInput"
            @blur="$emit('blur')"
          />
          <CustomButton
            :disabled="(isError || emailError) ? true : false"
            :type="isSubscriptionWidget ? 'quaternary' : 'primary'"
            :size="isSubscriptionWidget ? 'md' : 'lg'"
            :class="[
              {'w-full mt-2 lg:mt-4': !isSubscriptionWidget},
              {'max-w-max mb-5 min-h-[45px] min-w-[45px] lg:min-w-[200px]': isSubscriptionWidget},
            ]"
            data-testid="newsletterSignUpButton"
            :text="signupButtonText"
            :loading="isSending"
            :is-submit-type="true"
          >
            <span
              :class="{'hidden lg:block': isSubscriptionWidget}"
            >{{ signupButtonText }}</span>
            <i
              v-if="isSubscriptionWidget"
              class="iconify fa-pro-solid--paper-plane rotate-45 block lg:hidden"
            />
          </CustomButton>
        </div>
        <template v-if="emailError && isNewsletterTouched">
          <slot
            name="error-details"
            :error="emailError"
          />
        </template>
      </form>
      <template v-if="isError">
        <slot name="newsletter-error" />
      </template>
      <slot name="privacy" />
    </template>
    <template v-if="showPendingConfirmation">
      <div data-testid="confirmationEmailSentMessage">
        <slot name="thank-you" />
      </div>
    </template>
    <template v-if="showVoucher">
      <div data-testid="newsletterVoucher">
        <h3 class="text-2xl leading-7 mb-2 text-EXPONDO-grey text-center break-all max-w-max mx-auto uppercase">
          {{ $t('Thank you!') }}
        </h3>
        <div :class="{'text-sm': isSubscriptionWidget}">
          <p class="text-EXPONDO-grey mb-2 text-center">
            {{ $t('Your subscription has been successfully confirmed.') }}
          </p>
          <p class="text-EXPONDO-grey mb-2 text-center">
            {{ $t('Your code:') }}
          </p>
          <VoucherCode
            :voucher-code="subscriptionProgressData?.voucherCode"
            :voucher-valid-until="subscriptionProgressData?.voucherExpiryDate"
            @voucher-status="
              voucherStatus = $event;
              $emit('voucher-status', $event)
            "
          />
          <p
            v-if="voucherIsValid"
            class="text-EXPONDO-grey mt-4 text-center"
          >
            <i18n-t
            keypath="You can use vouchers until the date for purchases over XX!">
              <template #voucherExpiryDate>
                <span class="font-bold">{{ subscriptionProgressData?.voucherExpiryDate }}</span>
              </template>
              <template #minimumPurchaseValue>
                <span class="font-bold">{{ minimumPurchaseValue }}</span>
              </template>
            </i18n-t>
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import { useForm, useField } from 'vee-validate';
import { Logger } from '~~/helpers/logger';
import { ROUTES } from '~~/constants/routes';
import type { NewsletterActionType } from '~/composables/useCia/event/tracking/newsletter-doi';
import { NEWSLETTER_ACTION_WITHOUT_VOUCHER, NEWSLETTER_ACTION_WITH_VOUCHER } from '~~/constants/cia';

import { maskEmail } from '~~/helpers/maskEmail';
import { VOUCHER_STATUS, type VoucherStatus } from '~~/types/vouchers/voucher-status';

export default defineComponent({

  props: {
    signupButtonText: {
      type: String,
      required: true,
    },
    formClasses: {
      type: String,
      default: '',
    },
    newsletterActionType: {
      type: String as PropType<NewsletterActionType>,
      default: NEWSLETTER_ACTION_WITHOUT_VOUCHER,
    },
    source: {
      type: String,
      default: 'footer-newsletter',
    },
    inputId: {
      type: String,
      required: true,
    },
    isSubscriptionWidget: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['blur', 'input', 'subscription-progress-update', 'voucher-status'],
  setup(props, { emit }) {

    const { event: ciaEvent } = useCia();
    const { getAdjustedSlug } = useRouteExtended();
    const { ciaState } = useCiaState();
    const { domain } = useLocalization();
    const shopDomainWithPrefix = computed(() => `${domain.value}${getAdjustedSlug(ROUTES.HOME)}`);
    const {
      minimumPurchaseValue,
      voucherSettings,
      saveLocalSubscriptionProgress,
      getSubscriptionProgress,
    } = useNewsletterVoucher();

    const newsletterLink = computed(() => getAdjustedSlug(ROUTES.PRIVACY));
    const email = ref('');
    const emailMasked = computed(() => maskEmail(email.value));
    const isError = ref(false);
    const isSending = ref(false);
    const isNewsletterTouched = ref(false);
    const subscriptionProgressData = ref(null);
    const voucherStatus = ref<VoucherStatus>(VOUCHER_STATUS.valid);
    const voucherIsValid = computed(() => voucherStatus.value === VOUCHER_STATUS.valid);

    const { handleSubmit: formHandleSubmit } = useForm();

    const { errorMessage: emailError, setValue: setEmailValue } = useField('email', 'required|email', {
      initialValue: '',
    });

    watch(email, (newVal) => {

      setEmailValue(newVal);

    });

    const onEmailInput = (newValue: string) => {

      setEmailValue(newValue);
      emit('input', newValue);

    };

    const showSubscriptionForm = computed(() => {

      return subscriptionProgressData.value && (
        subscriptionProgressData.value.status === 'initiated' ||
        subscriptionProgressData.value.status === ''
      );
    
    });

    const showPendingConfirmation = computed(() => {

      return subscriptionProgressData.value &&
        subscriptionProgressData.value.status === 'submitted';
    
    });

    const showVoucher = computed(() => {

      return subscriptionProgressData.value &&
        subscriptionProgressData.value.status === 'voucher' &&
        props.newsletterActionType === NEWSLETTER_ACTION_WITH_VOUCHER;
    
    });

    const subscriptionWidgetClass = computed(() => {

      return props.isSubscriptionWidget && subscriptionProgressData.value !== null;
    
    });

    onMounted(async () => {

      subscriptionProgressData.value = await getSubscriptionProgress();
      emit('subscription-progress-update', subscriptionProgressData.value);
    
    });

    const handleSendingEmailSubscription = () => {

      if (!email.value) {

        isNewsletterTouched.value = true;

        return;
      
      }
      isSending.value = true;

      const subscribeWithVoucherParams = {
        emailMasked: emailMasked.value,
        source: props.source,
        shop: shopDomainWithPrefix.value,
        ...voucherSettings.value,
      };

      const payloadAdditionalParameters =
        props.newsletterActionType === NEWSLETTER_ACTION_WITH_VOUCHER
          ? subscribeWithVoucherParams
          : {};

      ciaEvent.newsletterDOI({
        email: email.value,
        action: props.newsletterActionType,
        userId: ciaState.value.userId,
        ...payloadAdditionalParameters,
      }).then(() => {

        isSending.value = false;
        saveLocalSubscriptionProgress({
          showFromDate: Math.floor(Date.now() / 1000),
          email: email.value,
          emailMasked: emailMasked.value,
          status: voucherSettings.value.status,
        });
        subscriptionProgressData.value.status = voucherSettings.value.status;
        subscriptionProgressData.value.emailMasked = emailMasked.value;
        emit('subscription-progress-update', subscriptionProgressData.value);
      
      }).catch((error) => {

        Logger.error(`cia|newsletterDOI error: ${error}`);
        isError.value = true;
        isSending.value = false;
      
      });
    
    };

    const onFormSubmit = formHandleSubmit(() => {

      handleSendingEmailSubscription();

    });

    return {
      email,
      emailError,
      isError,
      isSending,
      handleSendingEmailSubscription,
      onFormSubmit,
      onEmailInput,
      newsletterLink,
      isNewsletterTouched,
      minimumPurchaseValue,
      subscriptionProgressData,
      subscriptionWidgetClass,
      showSubscriptionForm,
      showPendingConfirmation,
      showVoucher,
      voucherStatus,
      voucherIsValid,
      NEWSLETTER_ACTION_WITH_VOUCHER,
    };
  
  },
});

</script>

<style lang="scss" scoped>

.newsletter {
  &__input {
    border-radius: var(--form-border-radius);
  }
}
</style>
